import i18next from 'i18next'
import moment, { Moment } from 'moment'

import 'moment/locale/fr'

import { PersistService } from 'src/data/common/PersistService'
import { Translatable } from 'src/data/common/type'

import countries from './countries.json'

const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const isProduction = (): boolean => process.env?.REACT_APP_ENV?.includes('production') === true

const getBrowserLang = (): string =>
  (navigator.userLanguage || navigator.language).substring(0, 2)

const trans = (toTraduct?: Translatable | string): string => {
  if (!toTraduct) return ''

  if (typeof toTraduct === 'object') {
    return toTraduct[i18next.language]
  } else {
    return toTraduct
  }
}

// react router considers plus sign as delimiter and parse the param to an array string by replacing the plus sign by a whitespace
const formatEmail = (email: string): string => {
  return decodeURIComponent(email).replace(/ /g, '+')
}

const formatPhoneNumber = (phoneNumberString: string): string => {
  var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = (match[1] ? '+1 ' : '');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return "";
}

// TODO: bring it into shopify service instead
const getShopifyBaseUrl = (): string => {
  const baseUrlCa = process.env.REACT_APP_BONLOOK_SHOPIFY_CA ?? ''
  const baseUrlUs = process.env.REACT_APP_BONLOOK_SHOPIFY_US ?? ''

  if (!isProduction()) {
    return "https://bonlook-ca-staging.myshopify.com"
  }
  if (!baseUrlCa && !baseUrlUs) {
    throw new Error('Baseurl either for Canada or USA is needed')
  }
  const market = PersistService.fetch('market', 'local') ?? 'CA'
  return market === 'CA' ? baseUrlCa : baseUrlUs
}

const isMobile = (): boolean => {
  return window.matchMedia('only screen and (max-device-width: 480px)').matches
}

const countryNamebyCode = (
  countryCode: string | null | undefined
): string | null => {
  if (!countryCode) return null
  if (countryCode && countryCode.length > 2) {
    return countryCode
  }
  const country = countries.find((country) => country.code === countryCode)
  return country ? trans(country.name) : null
}

const dateFormat = (
  date: Date | Moment | string,
  lang: string,
  format = 'MMM DD YYYY h:mm:ss'
): string => {
  const localeDate = moment(date).locale(lang)
  let formatedDate = ''
  if (lang === 'fr' && format === 'MMMM D, YYYY') {
    formatedDate = localeDate.format('D MMMM YYYY')
  } else {
    formatedDate = localeDate.format(format)
  }

  return capitalizeFirstLetter(formatedDate)
}

const formatPrice = (price: number, currency: string): string => {
  return `${currency.toUpperCase()} $${price.toFixed(2)}`
}

const getCurrentOrderStatus = (orderStatus: string): string => {
  switch (orderStatus) {
    case 'draft':
    case 'confirmed_cs':
    case 'call_dr':
    case 'confirmed_pro':
      return 'being_approved'
    case 'pushed':
    case 'in_transit':
    case 'distribution_centre':
      return 'being_processed'
    case 'store_transit':
    case 'store_received':
      return 'in_transit'
    case 'voided':
    case 'canceled':
      return 'canceled'
    case 'store_pickup':
      return 'schedule_your_appointment'
    case 'delivered':
      return 'delivered'
    case 'shipped':
      return 'shipped'
    default:
      return ''
  }
}

const openInvoice = (file: string): void => {
  let content = ''
  if (file) {
    content =
      '<iframe src="' +
      file +
      '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>'
  }
  const win = window.open('about:blank', 'My BonLook Receipt')
  win!.document.write(content)
}

const isFrench = (): boolean => i18next.language === 'fr'

const isvalidTimestamp = (timeStamp: string | null): boolean => {
  if (timeStamp != null) {
    const dt = new Date()
    const date = new Date(parseInt(timeStamp) * 1000)

    return date.setMinutes(date.getMinutes() + 1) >= dt.getTime()
  }
  return false
}

const getBase64 = (file: Blob): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    const image = document.createElement('img')
    reader.onload = (e) => {
      const targetResult = e.target?.result
      if (!targetResult) return
      image.src = targetResult as string
      image.onload = (): void => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(image, 0, 0)

        const MAX_WIDTH = 1000
        const MAX_HEIGHT = 900
        let width = image.width
        let height = image.height

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width
            width = MAX_WIDTH
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height
            height = MAX_HEIGHT
          }
        }
        canvas.width = width
        canvas.height = height

        ctx.drawImage(image, 0, 0, width, height)

        const dataurl = canvas.toDataURL('image/jpeg')
        resolve(dataurl)
      }
    }
    reader.onerror = (error) => reject(error)
  })
}

const convertUrlToBase64 = (url: string, callback: (url: string) => void) => {
  const image = document.createElement('img')
  image.src = url
  image.setAttribute('crossorigin', 'anonymous')
  image.onload = (): void => {
    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d")
    if (!context) return
    context.drawImage(image, 0, 0)

    let width = image.width
    let height = image.height

    canvas.style.width = width + 'px'
    canvas.style.height = height + 'px'
    canvas.width = width
    canvas.height = height

    context.drawImage(image, 0, 0, width, height)

    const dataURL = canvas.toDataURL('image/jpeg');
    callback(dataURL)
  }
}

const isFromCAWebsite = (): boolean =>
  PersistService.fetch('market', 'local') === 'CA'

const enum PaymentType {
  CREDIT,
  SEZZLE,
  GIFT_CARD,
}

const excludedObjectProps = (
  filteredProps: string[],
  originalObject: { [key: string]: any }
): { [key: string]: any } => {
  return Object.keys(originalObject)
    .filter((key) => !filteredProps.includes(key))
    .reduce((obj, key) => ({ ...obj, [key]: originalObject[key] }), {})
}

const onlyNumbers = (str: string): boolean => {
  const regex = new RegExp('^[0-9]+$')
  return regex.test(str)
}

const getAppUrl = (): string => {
  return process.env.REACT_APP_APP_URL!
}

const redirectToLogin = (email: string, params?: string): void => {
  PersistService.clearAll('local')

  let url = process.env.REACT_APP_SHOPIFY_PROXY_URL
    ? `${getShopifyBaseUrl()}/account/login`
    : `${getAppUrl()}?customerId=${email}`

  if (params) {
    url += params
  }

  window.location.href = url
}

export {
  capitalizeFirstLetter,
  countryNamebyCode,
  dateFormat,
  excludedObjectProps,
  formatEmail,
  formatPhoneNumber,
  formatPrice,
  getAppUrl,
  getBase64,
  convertUrlToBase64,
  getBrowserLang,
  getCurrentOrderStatus,
  getShopifyBaseUrl,
  isFrench,
  isFromCAWebsite,
  isMobile,
  isProduction,
  isvalidTimestamp,
  onlyNumbers,
  openInvoice,
  PaymentType,
  redirectToLogin,
  trans,
}
